/*
  Storie header. Logo, menu and icons share one row when the menu fits there (.storie-header--inline).
  Otherwise the menu gets its own row under the logo, in balanced lines if it is still too long.
  Below 1025px Ella hides this menu and shows its burger.
*/
(() => {
  const fit = () => {
    const header = document.getElementById('header-component');
    const columns = header?.querySelector('.header__row--top .header__columns');
    const menu = columns?.querySelector('.header__column--center .list-menu--inline');
    if (!menu) return;

    const widths = [...menu.children].map((item) => item.getBoundingClientRect().width);
    if (!widths.some(Boolean)) return; // Burger layout: the menu is hidden.

    const menuWidth = widths.reduce((sum, width) => sum + width, 0);
    const style = getComputedStyle(columns);
    const gap = parseFloat(style.columnGap) || 0;
    const rowWidth = columns.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const sides = [...columns.querySelectorAll(':scope > .header__column--left, :scope > .header__column--right')]
      .reduce((sum, column) => sum + column.offsetWidth + gap, 0);
    const inline = menuWidth <= rowWidth - sides;

    // When the menu wraps, use the narrowest width that keeps the same number of lines, so the lines come out even.
    const linesAt = (cap) => widths.reduce(([count, used], w) => (used && used + w > cap ? [count + 1, w] : [count, used + w]), [1, 0])[0];
    const lines = inline ? 1 : linesAt(rowWidth);
    let cap = Math.ceil(Math.max(menuWidth / lines, ...widths)) + 1;
    while (lines > 1 && linesAt(cap) > lines) cap += 8;
    menu.style.maxWidth = lines > 1 ? `${cap}px` : '';

    header.classList.toggle('storie-header--inline', inline);

    // Header pebbles: centered in the space between the logo and the menu, straddling the bottom edge. They hide
    // when the menu has its own row or that space is too narrow. Together they are about 1.7 times the size setting.
    const decor = header.querySelector('.storie-header-decor');
    if (decor) {
      const logoRight = columns.querySelector(':scope > .header__column--left').getBoundingClientRect().right;
      // The size comes from the setting, since a hidden element measures 0.
      const size = parseFloat(getComputedStyle(decor).getPropertyValue('--storie-header-decor-size')) || 72;
      const room = menu.getBoundingClientRect().left - logoRight - size * 1.7;
      const fits = inline && room > 0;
      decor.classList.toggle('storie-header-decor--hidden', !fits);
      if (fits) decor.style.left = `${logoRight - header.getBoundingClientRect().left + room / 2}px`;
    }
  };

  // A dropdown panel opens under its menu link. It shifts left when it would pass the right edge of the window.
  // The observer runs when a panel opens, closes or gets its links, which arrive after the first hover.
  const keepInside = (panel) => {
    panel.style.marginInlineStart = '';
    const overflow = panel.getBoundingClientRect().right - (document.documentElement.clientWidth - 16);
    if (overflow > 0) panel.style.marginInlineStart = `${-overflow}px`;
  };
  const panels = new ResizeObserver((entries) => entries.forEach((entry) => keepInside(entry.target)));
  const watchPanels = () =>
    document
      .querySelectorAll('#header-component .list-menu--inline > li > details > .details__list')
      .forEach((panel) => panels.observe(panel));

  // Signed-in account avatar. Shopify draws it in the open shadow root of <shopify-account>, out of reach of the
  // page CSS, with a fixed faint ring and its own hover. This style gives it a ring in its text color and the hover
  // of the other header icons. The component renders into its own wrapper, so the style stays in place.
  // The :host and .account-button prefixes outrank the component's sheets, which come later in the cascade.
  const avatarStyle = `
    :host .account-button .account-button__avatar {
      box-shadow: inset 0 0 0 1.5px currentColor;
      scale: none;
      transition: scale var(--animation-speed, 0.2s) var(--animation-easing, ease);
    }
    :host .account-button .account-button__avatar::before { display: none; }
    :host .account-button__avatar-initials { line-height: 1; text-box: trim-both cap alphabetic; }
    @media (hover: hover) and (min-width: 1025px) {
      :host .account-button:hover .account-button__avatar { scale: 1.15; }
    }
    /* Desktop header icons are 20% smaller (storie-brand.css): 28px becomes 22.4px. */
    @media (min-width: 1025px) {
      :host .account-button { --clamped-avatar-size: 2.24rem; }
    }
  `;
  customElements.whenDefined('shopify-account').then(() => {
    document.querySelectorAll('shopify-account').forEach(({ shadowRoot }) => {
      if (!shadowRoot || shadowRoot.querySelector('style[data-storie]')) return;
      const style = document.createElement('style');
      style.dataset.storie = '';
      style.textContent = avatarStyle;
      shadowRoot.append(style);
    });
  });

  fit();
  watchPanels();
  window.addEventListener('resize', fit);
  document.addEventListener('shopify:section:load', () => {
    fit();
    watchPanels();
  });
  document.fonts?.ready.then(fit);
})();

/*
  Language link (snippets/storie-language-link.liquid): point it at this page in the other language,
  from the hreflang alternates Shopify prints in the head ("fr", or "en-GB" and the other English
  countries, which share one address).
*/
(() => {
  const alternate = (target) =>
    [...document.querySelectorAll('link[rel="alternate"][hreflang]')].find(
      (alt) => alt.hreflang === target || alt.hreflang.startsWith(`${target}-`)
    );
  const point = (link) => {
    const alt = alternate(link.getAttribute('hreflang'));
    if (alt) link.href = alt.href;
  };
  document.querySelectorAll('[data-storie-language-link]').forEach(point);
  // The mobile menu arrives after this script: point its link when it is used.
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-storie-language-link]');
    if (link) point(link);
  }, true);
})();
